import React, {
  useContext,
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
} from "react";
import PropTypes from "prop-types";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { DialogActions, Grid, Typography, Box } from "@mui/material";
import { ModalContext } from "../ModalContext";
import { UserContext } from "../../../context/userContext";
import DealerActionsClient from "../../../../clients/DealerActionsClient";
import EMPButton from "../../EMPButton/EMPButton";
import EMPCheckbox from "../../EMPCheckbox/EMPCheckbox";
import EMPLoader from "../../EMPLoader/EMPLoader";
import { DEALER_ACTIONS } from "../../../../common/Constants";
import { useFieldSupportStatus } from "../../../hooks/useFieldSupportStatus";
import { usePermission } from "../../../hooks/usePermission";
import {
  trackAcceptPurchaseDepositPopupAction,
  trackAcceptPurchaseDepositPopupLoad,
} from "../../../services/analytics/OrderDetailsPageAnalytics";
import { useVehicleData } from "../../../hooks/useVehicleData";

const AcceptPurchaseDeposit = ({ orderId, backButton, handleBack }) => {
  const { handleModal } = useContext(ModalContext);
  const { user } = useContext(UserContext);
  const { market, dealerId, salesCode, brand, userId } = user;
  const userCommonId = `${market}|${dealerId}|${salesCode}|${brand}`;
  const [checked, setChecked] = useState(false);
  const [disabled, setDisabled] = useState(true);
  const queryClient = useQueryClient();
  const contentRef = useRef(null);
  const [contentHeight, setContentHeight] = useState(0);
  const { t } = useTranslation();
  const isFieldSupport = useFieldSupportStatus();
  const canAcceptPurchaseDeposit = usePermission("acceptDepositNA:write");
  const { nameplate, modelYear } = useVehicleData(orderId);

  useEffect(() => {
    if (isFieldSupport) {
      return;
    }

    setDisabled(!checked);
  }, [isFieldSupport, checked]);

  useEffect(() => {
    trackAcceptPurchaseDepositPopupLoad(orderId, user, modelYear, nameplate);
  }, [orderId, user, modelYear, nameplate]);

  useEffect(() => {
    setDisabled(!checked);
  }, [checked]);

  useLayoutEffect(() => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.offsetHeight);
    }
  });

  const handleChange = () => {
    setChecked(!checked);
  };

  const handleErrorMessage = () => {
    let errorMessage = "An error occurred. Please try again.";

    if (submitAccept.error?.response?.data) {
      try {
        const errorData = submitAccept.error.response.data.message;
        errorMessage =
          typeof errorData === "string" ? JSON.parse(errorData) : errorData;
        return errorMessage.errors[0].message;
      } catch (e) {
        console.error("Failed to parse error message:", e);
      }
    }

    return errorMessage;
  };

  const submitAccept = useMutation({
    mutationFn: (actionData) =>
      DealerActionsClient.actionOnOrder(userCommonId, actionData, userId),
    onSuccess: () => {
      handleModal(false, false);
      trackAcceptPurchaseDepositPopupAction("confirm", orderId, user);
      queryClient.invalidateQueries({ queryKey: ["order", { orderId }] });
      queryClient.invalidateQueries({
        queryKey: ["notifications", { orderId }],
      });
    },
    onError: (error) => {
      if (error.response?.status === 400) {
        const errorMessage = error.response.data.message;

        console.log(`400 Error: ${errorMessage}`);
      } else {
        console.log(error);
      }
    },
  });

  let modalContent;

  if (submitAccept.isLoading) {
    modalContent = (
      <Box sx={{ minHeight: contentHeight, py: 10 }}>
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          sx={{ height: "100%" }}
        >
          <Grid item>
            <EMPLoader loadingMessage="Submitting..." />
          </Grid>
        </Grid>
      </Box>
    );
  } else if (submitAccept.isError) {
    modalContent = (
      <Box sx={{ minHeight: contentHeight, pb: 4 }}>
        <Typography variant="body2" sx={{ lineBreak: "anywhere" }}>
          {handleErrorMessage()}
        </Typography>
      </Box>
    );
  } else {
    modalContent = (
      <Box ref={contentRef}>
        <Typography variant="body1" sx={{ mb: 6 }}>
          {t("financing.acceptDepositModal.message")}
        </Typography>

        <Box sx={{ mb: 10 }}>
          <ul style={{ paddingLeft: "12px" }}>
            <li>
              <Typography variant="body2">
                I accept the deposit from the customer
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                Dealership merchant fees may apply to this transaction
              </Typography>
            </li>
          </ul>
        </Box>

        <Box sx={{ mb: 4 }}>
          <EMPCheckbox
            label={t("financing.acceptDepositModal.checkboxConfirm")}
            checked={checked}
            handleChange={() => handleChange()}
          />
        </Box>
      </Box>
    );
  }

  return (
    <div>
      {modalContent}

      <DialogActions sx={{ padding: "16px 0" }}>
        <Grid container justifyContent="space-between">
          <Grid item>
            <EMPButton
              variant="text"
              onClick={() => {
                if (backButton) {
                  handleBack();
                } else {
                  handleModal(false, false);
                }

                trackAcceptPurchaseDepositPopupAction("close", orderId, user);
              }}
              disabled={submitAccept.isLoading}
            >
              {backButton ? "Back" : t("common.cancel")}
            </EMPButton>
          </Grid>
          <Grid item>
            <EMPButton
              variant="contained"
              onClick={() =>
                submitAccept.mutate({
                  reservationId: orderId,
                  dealerAction: DEALER_ACTIONS.PURCHASE_DEPOSIT_ACCEPTED,
                })
              }
              disabled={
                disabled ||
                submitAccept.isLoading ||
                submitAccept.isError ||
                !canAcceptPurchaseDeposit
              }
            >
              {t("common.buttonCtas.acceptPurchaseDeposit")}
            </EMPButton>
          </Grid>
        </Grid>
      </DialogActions>
    </div>
  );
};

AcceptPurchaseDeposit.propTypes = {
  orderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  backButton: PropTypes.bool,
  handleBack: PropTypes.func,
};

export default AcceptPurchaseDeposit;
